import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { Search, Edit2, Save, X } from 'lucide-react';

const BrandActivity = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search term
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.brandName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const usersCollection = collection(db, 'users');
      const userSnapshot = await getDocs(usersCollection);
      const userList = userSnapshot.docs.map(doc => ({
        id: doc.id,
        brandName: doc.data().brandName || 'NA',
        phoneNumber: doc.data().phoneNumber || 'NA',
        emailAddress: doc.data().emailAddress || 'NA',
        gstNumber: doc.data().gstNumber || 'NA',
        shop: doc.data().shop || 'NA',
        address: doc.data().address || 'NA',
        city: doc.data().city || 'NA',
        pincode: doc.data().pincode || 'NA',
        state: doc.data().state || 'NA'
      }));
      setUsers(userList);
      setFilteredUsers(userList);
    } catch (error) {
      console.error("Error fetching users data: ", error);
      alert('Error fetching data. Please refresh the page.');
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setEditFormData({ ...user });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!editingId) return;

    setSaving(true);
    try {
      const userRef = doc(db, 'users', editingId);
      
      // Update only the fields that should be updated
      await updateDoc(userRef, {
        brandName: editFormData.brandName,
        phoneNumber: editFormData.phoneNumber,
        emailAddress: editFormData.emailAddress,
        gstNumber: editFormData.gstNumber || '',
        shop: editFormData.shop || '',
        address: editFormData.address || '',
        city: editFormData.city || '',
        pincode: editFormData.pincode || '',
        state: editFormData.state || ''
      });

      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === editingId ? { ...editFormData } : user
      ));

      setEditingId(null);
      setEditFormData({});
      alert('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const downloadExcel = () => {
    if (filteredUsers.length === 0) {
      alert('No data available to download');
      return;
    }

    setLoading(true);

    try {
      const excelData = filteredUsers.map((user, index) => ({
        'S.No': index + 1,
        'Brand Name': user.brandName || 'NA',
        'Phone Number': user.phoneNumber || 'NA',
        'Email Address': user.emailAddress || 'NA',
        'Shop': user.shop || 'NA',
        'Address': user.address || 'NA',
        'City': user.city || 'NA',
        'Pincode': user.pincode || 'NA',
        'State': user.state || 'NA',
        'GST Number': user.gstNumber || 'NA'
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      const colWidths = [
        { wch: 8 },  // S.No
        { wch: 20 }, // Brand Name
        { wch: 15 }, // Phone Number
        { wch: 25 }, // Email Address
        { wch: 20 }, // Shop
        { wch: 35 }, // Address
        { wch: 15 }, // City
        { wch: 10 }, // Pincode
        { wch: 15 }, // State
        { wch: 20 }  // GST Number
      ];
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Brand Activity Report');

      const date = new Date();
      const filename = `Brand_Activity_Report_${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}.xlsx`;

      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      alert('Error downloading file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
        <h2 style={{ margin: 0 }}>Brand Activity</h2>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search Box */}
          <div style={{ position: 'relative', minWidth: '250px' }}>
            <Search 
              size={18} 
              style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#666' }}
            />
            <input
              type="text"
              placeholder="Search by brand name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '10px 10px 10px 35px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                fontSize: '14px',
                width: '100%',
                outline: 'none'
              }}
            />
          </div>

          {/* Download Button */}
          <button
            onClick={downloadExcel}
            disabled={loading || filteredUsers.length === 0}
            style={{
              padding: '10px 20px',
              backgroundColor: loading ? '#ccc' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: loading || filteredUsers.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {loading ? (
              <>
                <span>⏳</span>
                Downloading...
              </>
            ) : (
              <>
                <span>📊</span>
                Download Report
              </>
            )}
          </button>
        </div>
      </div>

      {searchTerm && (
        <p style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
          Found {filteredUsers.length} result{filteredUsers.length !== 1 ? 's' : ''} for "{searchTerm}"
        </p>
      )}
      
      <div style={{ overflowX: 'auto' }}>
        {filteredUsers.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', minWidth: '1200px' }}>
            <thead>
              <tr>
                <th style={{ padding: '10px', border: '1px solid #ddd', backgroundColor: '#f4f4f4', fontWeight: 'bold' }}>Actions</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', backgroundColor: '#f4f4f4', fontWeight: 'bold' }}>Brand Name</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', backgroundColor: '#f4f4f4', fontWeight: 'bold' }}>Phone Number</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', backgroundColor: '#f4f4f4', fontWeight: 'bold' }}>Email Address</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', backgroundColor: '#f4f4f4', fontWeight: 'bold' }}>Shop</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', backgroundColor: '#f4f4f4', fontWeight: 'bold' }}>Address</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', backgroundColor: '#f4f4f4', fontWeight: 'bold' }}>City</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', backgroundColor: '#f4f4f4', fontWeight: 'bold' }}>Pincode</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', backgroundColor: '#f4f4f4', fontWeight: 'bold' }}>State</th>
                <th style={{ padding: '10px', border: '1px solid #ddd', backgroundColor: '#f4f4f4', fontWeight: 'bold' }}>GST Number</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id} style={{ backgroundColor: editingId === user.id ? '#f9f9f9' : '#fff' }}>
                  <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                    {editingId === user.id ? (
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                            fontSize: '12px'
                          }}
                        >
                          <Save size={14} />
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancel}
                          disabled={saving}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                            fontSize: '12px'
                          }}
                        >
                          <X size={14} />
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEdit(user)}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: '#2196F3',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                          fontSize: '12px',
                          margin: '0 auto'
                        }}
                      >
                        <Edit2 size={14} />
                        Edit
                      </button>
                    )}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                    {editingId === user.id ? (
                      <input
                        type="text"
                        value={editFormData.brandName}
                        onChange={(e) => handleInputChange('brandName', e.target.value)}
                        style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '3px' }}
                      />
                    ) : (
                      user.brandName
                    )}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                    {editingId === user.id ? (
                      <input
                        type="text"
                        value={editFormData.phoneNumber}
                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                        style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '3px' }}
                      />
                    ) : (
                      user.phoneNumber
                    )}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                    {editingId === user.id ? (
                      <input
                        type="email"
                        value={editFormData.emailAddress}
                        onChange={(e) => handleInputChange('emailAddress', e.target.value)}
                        style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '3px' }}
                      />
                    ) : (
                      user.emailAddress
                    )}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                    {editingId === user.id ? (
                      <input
                        type="text"
                        value={editFormData.shop}
                        onChange={(e) => handleInputChange('shop', e.target.value)}
                        style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '3px' }}
                      />
                    ) : (
                      user.shop || 'NA'
                    )}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                    {editingId === user.id ? (
                      <input
                        type="text"
                        value={editFormData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '3px' }}
                      />
                    ) : (
                      user.address || 'NA'
                    )}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                    {editingId === user.id ? (
                      <input
                        type="text"
                        value={editFormData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '3px' }}
                      />
                    ) : (
                      user.city || 'NA'
                    )}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                    {editingId === user.id ? (
                      <input
                        type="text"
                        value={editFormData.pincode}
                        onChange={(e) => handleInputChange('pincode', e.target.value)}
                        style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '3px' }}
                      />
                    ) : (
                      user.pincode || 'NA'
                    )}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                    {editingId === user.id ? (
                      <input
                        type="text"
                        value={editFormData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '3px' }}
                      />
                    ) : (
                      user.state || 'NA'
                    )}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                    {editingId === user.id ? (
                      <input
                        type="text"
                        value={editFormData.gstNumber}
                        onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                        style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '3px' }}
                      />
                    ) : (
                      user.gstNumber || 'NA'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            {searchTerm ? `No users found matching "${searchTerm}"` : 'No users found'}
          </p>
        )}
      </div>
    </div>
  );
};

export default BrandActivity;